import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Form, Row, Col, Image, Button } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import Flex from 'components/common/Flex';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import UserContext from 'app/context/UserContext';
import { upload } from 'app/api/UploadsService';
import {
  updateHeroImagesScroller,
  getHeroImagesScroller
} from 'app/api/PublicOptionsService';
import axios from 'axios';
import { toast } from 'react-toastify';

function HeroImagesScroller() {
  const { user } = useContext(UserContext);

  const [previewUrls, setPreviewUrls] = useState([]);
  const [newScrollerImages, setNewScrollerImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [saving, setSaving] = useState(false);

  /* ================= FETCH SAVED IMAGES ================= */
  const fetchImages = async () => {
    try {
      const res = await axios(getHeroImagesScroller(user.token));
      const images = res?.data?.data?.images || res?.data?.data || [];
      setExistingImages(images);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch saved images");
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  /* ================= DROPZONE → UPLOAD IMMEDIATELY ================= */
  const onDropFiles = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles?.length) return;

    try {
      // preview images
      const previews = acceptedFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...previews]);

      const uploadedImages = [];

      for (let i = 0; i < acceptedFiles.length; i++) {
        const formData = new FormData();
        formData.append("file", acceptedFiles[i]);

        const res = await upload(user.token, formData);
        const uploadData = res.data;

        uploadedImages.push({
          scrollerImageId: `img_${Date.now()}_${i}`,
          scrollerImageUrl: uploadData.relativePath,
          scrollerImagePath: uploadData.path,
        });
      }

      setNewScrollerImages(prev => [...prev, ...uploadedImages]);
      toast.success(`${uploadedImages.length} image(s) uploaded`);

    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
    }
  }, [user.token]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDropFiles,
    multiple: true,
    accept: "image/*"
  });

  /* ================= SUBMIT → UPSERT API ================= */
  const handleSubmit = async () => {
    if (!newScrollerImages.length) {
      toast.warning("Please upload images first");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        images: newScrollerImages
      };

      await axios(updateHeroImagesScroller(user.token, payload));

      toast.success("Hero images saved successfully!");

      // reset new uploads
      setPreviewUrls([]);
      setNewScrollerImages([]);

      // 🔥 REFRESH SAVED IMAGES
      fetchImages();

    } catch (err) {
      console.error(err);
      toast.error("Failed to save images");
    } finally {
      setSaving(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div>
      <Row className="g-4 align-items-start">

        {/* DROPZONE */}
        <Col md={6}>
          <Form.Label>Upload Hero Images</Form.Label>
          <div
            {...getRootProps()}
            style={{
              height: 250,
              borderRadius: 6,
              cursor: 'pointer',
              border: '1px dashed #ccc',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <input {...getInputProps()} />
            <Flex direction="column" alignItems="center">
              <img src={cloudUpload} alt="upload" width={40} className="mb-2" />
              <p>Drop multiple images here</p>
            </Flex>
          </div>
        </Col>

        {/* NEW PREVIEW */}
        <Col md={6}>
          <Form.Label>New Images Preview</Form.Label>
          <div style={{
            height: 250,
            overflowX: 'auto',
            display: 'flex',
            gap: 12,
            padding: 10,
            border: '1px solid #eee',
            borderRadius: 6
          }}>
            {previewUrls.length ? previewUrls.map((url, i) => (
              <Image key={i} src={url} style={{ height: 200, borderRadius: 6 }} />
            )) : <p className="text-muted">No images selected</p>}
          </div>
        </Col>
      </Row>

      

      {/* SAVED IMAGES */}
      <hr className="my-4" />
      <Form.Label>Saved Hero Images</Form.Label>

      <div style={{
        display: 'flex',
        gap: 16,
        overflowX: 'auto',
        padding: 10,
        border: '1px solid #eee',
        borderRadius: 6
      }}>
        {existingImages.length ? existingImages.map((img, i) => (
          <Image
            key={i}
            src={img.scrollerImageUrl}
            style={{ height: 220, borderRadius: 6 }}
          />
        )) : (
          <p className="text-muted">No saved images</p>
        )}
      </div>
      {/* SAVE BUTTON */}
      <div className="text-center mt-4">
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}

export default HeroImagesScroller;
